import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { exportFoods } from 'src/api/foods';

// ----------------------------------------------------------------------
// Template URLs - Update these with your Google Drive links
// ----------------------------------------------------------------------

const EASY_TEMPLATE_URL = 'YOUR_GOOGLE_DRIVE_EASY_TEMPLATE_URL_HERE';
const COMPLEX_TEMPLATE_URL = 'YOUR_GOOGLE_DRIVE_COMPLEX_TEMPLATE_URL_HERE';

// ----------------------------------------------------------------------

type FoodExportModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  restaurantId: string;
};

export default function FoodExportModal({
  open,
  onClose,
  onSuccess,
  restaurantId,
}: FoodExportModalProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [exportType, setExportType] = useState<'easy' | 'complex'>('easy');
  const [loading, setLoading] = useState<boolean>(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setExportType('easy');
    }
  }, [open]);

  const handleDownloadTemplate = (type: 'easy' | 'complex') => {
    const url = type === 'easy' ? EASY_TEMPLATE_URL : COMPLEX_TEMPLATE_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Map 'easy' to 'simple' for API compatibility
      const mode = exportType === 'easy' ? 'simple' : 'complex';

      // Call the export API
      const { blob, filename } = await exportFoods(restaurantId, mode);

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        `${exportType === 'easy' ? 'Easy' : 'Complex'} export completed successfully`,
        {
          variant: 'success',
        }
      );

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Export operation failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Iconify icon="mdi:file-export-outline" />
          <Typography variant="h6">Export Food</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Export Type Selection */}
          <FormControl component="fieldset">
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select Export Type
            </Typography>
            <RadioGroup
              value={exportType}
              onChange={(e) => setExportType(e.target.value as 'easy' | 'complex')}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: exportType === 'easy' ? 2 : 1,
                      borderColor: exportType === 'easy' ? 'success.main' : 'divider',
                      '&:hover': {
                        borderColor: 'success.main',
                      },
                    }}
                    onClick={() => setExportType('easy')}
                  >
                    <FormControlLabel
                      value="easy"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:lightning-bolt" width={20} color="success.main" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Easy Export
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Export basic food information only
                          </Typography>
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                    
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: exportType === 'complex' ? 2 : 1,
                      borderColor: exportType === 'complex' ? 'warning.main' : 'divider',
                      '&:hover': {
                        borderColor: 'warning.main',
                      },
                    }}
                    onClick={() => setExportType('complex')}
                  >
                    <FormControlLabel
                      value="complex"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:cog" width={20} color="warning.main" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Complex Export
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Export complete food data with all details
                          </Typography>
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Card>
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>

          {/* Helper Information */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.neutral',
              borderRadius: 1,
              border: '1px solid',
              borderColor: exportType === 'easy' ? 'success.main' : 'warning.main',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {exportType === 'easy' ? 'Easy Export Details' : 'Complex Export Details'}
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {exportType === 'easy' ? (
                <>
                  <li>
                    <Typography variant="body2">
                      Basic Food information: name, description
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Meals: Breakfast, Lunch, Dinner, Snack and similar.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                     Plan type: Meals tagged to plan types such as Muscle Building, Weight Loss etc
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Availabiliy: Only 1 week repeated patterns will be exported ignore any special cases like custom dates
                    </Typography>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Typography variant="body2">
                      Exports complete food data including categories and multiple sheets
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Larger Excel file with comprehensive information
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Perfect for detailed analysis and complete backups
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Returns, each section information and doesnt misses any record. 
                    </Typography>
                  </li>
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          color={exportType === 'easy' ? 'success' : 'warning'}
          startIcon={<Iconify icon="mdi:download" />}
        >
          {exportType === 'easy' ? 'Export (Easy)' : 'Export (Complex)'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
